import { z } from 'zod';
import { SALES_METRIC_IDS } from './sales-metrics.js';
import { TARGET_METRICS } from './sales-targets.js';
import { TRAFFIC_METRIC_IDS } from './traffic-metrics.js';
const metric = z.enum(SALES_METRIC_IDS);
const targetMetric = z.enum(TARGET_METRICS);
const tileVariant = z.enum(['tile', 'full']);
function define(def) {
    return { ...def, defaultConfig: def.configSchema.parse({}) };
}
export const WIDGET_DEFINITIONS = [
    define({
        id: 'kpi',
        title: 'KPI',
        category: 'headline',
        description: 'One figure, its change against the comparison period, and a sparkline.',
        configSchema: z.object({
            metric: metric.default('net_sales'),
            /** Shown instead when the metric has no value (no fee data yet, say). */
            fallbackMetric: metric.optional(),
            sparkline: z.boolean().default(true),
        }),
        defaultSize: { w: 3, h: 2 },
        minSize: { w: 2, h: 2 },
        siteAware: true,
    }),
    define({
        id: 'insights',
        title: 'Insights',
        category: 'insights',
        description: 'What changed and what to do about it: surges, slumps, price-change effects, targets at risk, data problems.',
        configSchema: z.object({
            audience: z.enum(['all', 'executive', 'operations', 'pricing']).default('all'),
            limit: z.number().int().min(3).max(20).default(6),
        }),
        defaultSize: { w: 4, h: 6 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'target-pace',
        title: 'Target pace',
        category: 'goals',
        description: 'Sold so far against the target and where the forecast says the period will land.',
        configSchema: z.object({
            period: z.enum(['month', 'quarter', 'year']).default('month'),
            metric: targetMetric.default('net_sales'),
            variant: tileVariant.default('full'),
        }),
        defaultSize: { w: 8, h: 4 },
        minSize: { w: 3, h: 2 },
        siteAware: true,
    }),
    define({
        id: 'forecast',
        title: 'Forecast',
        category: 'goals',
        description: 'Where this month, quarter and year are heading, with the likely range.',
        configSchema: z.object({ metric: targetMetric.default('net_sales') }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'forecast-accuracy',
        title: 'Forecast accuracy',
        category: 'goals',
        description: 'How close the forecast came on past periods, made on day 10 and day 20.',
        configSchema: z.object({ period: z.enum(['month', 'quarter']).default('month'), metric: targetMetric.default('net_sales') }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'metric-trend',
        title: 'Trend',
        category: 'trends',
        description: 'A metric over time against the comparison period, optionally split by site or channel.',
        configSchema: z.object({
            metric: metric.default('net_sales'),
            groupBy: z.enum(['none', 'site', 'channel']).default('none'),
            chart: z.enum(['line', 'bar']).default('line'),
        }),
        defaultSize: { w: 8, h: 4 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'site-split',
        title: 'Sales by site',
        category: 'trends',
        description: 'How the figure divides between MLF, MFF and sales no site could be found for.',
        configSchema: z.object({ metric: metric.default('net_sales') }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: false,
    }),
    define({
        id: 'channel-mix',
        title: 'Sales by channel',
        category: 'trends',
        description: 'Each channel’s share, with its change against the comparison period.',
        configSchema: z.object({ metric: metric.default('net_sales') }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'breakdown-table',
        title: 'Breakdown',
        category: 'trends',
        description: 'Net sales, orders and units by channel, site, ship-to state or order status.',
        configSchema: z.object({ dimension: z.enum(['channel', 'site', 'region', 'status']).default('channel') }),
        defaultSize: { w: 6, h: 4 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'seasonality',
        title: 'Seasonality',
        category: 'trends',
        description: 'Each month side by side across years.',
        configSchema: z.object({ metric: metric.default('net_sales'), years: z.number().int().min(2).max(10).default(3) }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'dow-hour-heatmap',
        title: 'When orders arrive',
        category: 'trends',
        description: 'Day of week against hour of day, New York time.',
        configSchema: z.object({ metric: z.enum(['orders', 'net_sales']).default('orders') }),
        defaultSize: { w: 6, h: 4 },
        minSize: { w: 5, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'traffic-kpi',
        title: 'Traffic KPI',
        category: 'traffic',
        description: 'Site conversion, visits or another Google Analytics figure — with a sales figure to show instead where GA is not connected.',
        configSchema: z.object({
            metric: z.enum(TRAFFIC_METRIC_IDS).default('site_conversion'),
            fallbackMetric: metric.optional(),
            sparkline: z.boolean().default(true),
        }),
        defaultSize: { w: 3, h: 2 },
        minSize: { w: 2, h: 2 },
        siteAware: true,
    }),
    define({
        id: 'conversion-trend',
        title: 'Conversion over time',
        category: 'traffic',
        description: 'Visits and website orders over time, and the conversion between them.',
        configSchema: z.object({
            metric: z.enum(['site_conversion', 'sessions', 'revenue_per_session']).default('site_conversion'),
            groupBy: z.enum(['none', 'site']).default('none'),
        }),
        defaultSize: { w: 8, h: 4 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'funnel',
        title: 'Shopping funnel',
        category: 'traffic',
        description: 'Visits that viewed a product, added to cart, started checkout and bought.',
        configSchema: z.object({}),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'traffic-sources',
        title: 'Where visits come from',
        category: 'traffic',
        description: 'Visits by channel group or source / medium, with what GA recorded them buying.',
        configSchema: z.object({
            by: z.enum(['channel_group', 'source_medium']).default('channel_group'),
            limit: z.number().int().min(3).max(25).default(8),
        }),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'ga-reconciliation',
        title: 'GA against orders',
        category: 'operations',
        description: 'Purchases Google Analytics recorded against real website orders — a check on the tracking.',
        configSchema: z.object({}),
        defaultSize: { w: 6, h: 4 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'sku-conversion',
        title: 'Product views and sales',
        category: 'products',
        description: 'How often each product is viewed, added to cart and bought.',
        configSchema: z.object({
            sort: z.enum(['views', 'conversion']).default('views'),
            limit: z.number().int().min(3).max(50).default(12),
        }),
        defaultSize: { w: 8, h: 5 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'proceeds-waterfall',
        title: 'From gross to net',
        category: 'money',
        description: 'Gross sales, less discounts and refunds, to net sales — then shipping and fees where known.',
        configSchema: z.object({}),
        defaultSize: { w: 4, h: 4 },
        minSize: { w: 3, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'top-products',
        title: 'Top products',
        category: 'products',
        description: 'Best sellers in the range, with the change against the comparison period.',
        configSchema: z.object({
            metric: z.enum(['net_sales', 'units']).default('net_sales'),
            limit: z.number().int().min(3).max(50).default(10),
        }),
        defaultSize: { w: 6, h: 5 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'top-movers',
        title: 'Biggest movers',
        category: 'products',
        description: 'Products whose sales rose or fell the most against the comparison period.',
        configSchema: z.object({ limit: z.number().int().min(3).max(25).default(8) }),
        defaultSize: { w: 6, h: 5 },
        minSize: { w: 4, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'price-impact',
        title: 'Price changes',
        category: 'products',
        description: 'Recent price changes and what sales did after each, allowing for how the rest of the channel moved.',
        configSchema: z.object({ days: z.number().int().min(30).max(365).default(120) }),
        defaultSize: { w: 12, h: 5 },
        minSize: { w: 5, h: 3 },
        siteAware: true,
    }),
    define({
        id: 'dead-stock',
        title: 'Not selling',
        category: 'products',
        description: 'Live listings with no sale in the dead-stock window (Admin → Sales data), on channels whose orders are imported.',
        configSchema: z.object({ variant: tileVariant.default('full') }),
        defaultSize: { w: 6, h: 5 },
        minSize: { w: 3, h: 2 },
        siteAware: true,
    }),
    define({
        id: 'open-orders',
        title: 'Open orders',
        category: 'operations',
        description: 'Placed but not yet shipped, by age — and balances still owed.',
        configSchema: z.object({ variant: tileVariant.default('full') }),
        defaultSize: { w: 6, h: 4 },
        minSize: { w: 3, h: 2 },
        siteAware: true,
    }),
    define({
        id: 'sales-review',
        title: 'Sales review',
        category: 'operations',
        description: 'Orders waiting on a decision, and the money that depends on them.',
        configSchema: z.object({ variant: tileVariant.default('tile') }),
        defaultSize: { w: 3, h: 2 },
        minSize: { w: 3, h: 2 },
        siteAware: false,
    }),
    define({
        id: 'data-coverage',
        title: 'Data coverage',
        category: 'operations',
        description: 'Each channel’s import: how far back it reaches, when it last ran, and anything wrong.',
        configSchema: z.object({ variant: tileVariant.default('full') }),
        defaultSize: { w: 12, h: 3 },
        minSize: { w: 3, h: 2 },
        siteAware: false,
    }),
    define({
        id: 'section-header',
        title: 'Section header',
        category: 'layout',
        description: 'A heading that labels the widgets beneath it.',
        configSchema: z.object({ text: z.string().max(80).default('Section') }),
        defaultSize: { w: 12, h: 1 },
        minSize: { w: 2, h: 1 },
        siteAware: false,
    }),
];
export const WIDGETS_BY_ID = new Map(WIDGET_DEFINITIONS.map((w) => [w.id, w]));
//# sourceMappingURL=dashboard-widgets.js.map